import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Clock3, Plus, Search, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  TableSkeleton,
  cx,
} from "../components/ui";
import { formatDate } from "../lib/format";
import {
  LEAVE_LABEL,
  LEAVE_TONE,
  isManagerOrAbove,
} from "../lib/people";
import { usePersona } from "../lib/persona";
import type {
  LeaveBalance,
  LeaveBalanceAuditRow,
  LeaveQuotaItem,
  LeaveRequest,
  LeaveType,
} from "../api/types";

type Tab = "requests" | "mine" | "team" | "types" | "balances";

const LEAVE_TABS: Tab[] = ["requests", "mine", "team", "types", "balances"];

function isKnownTab(value: string | null): value is Tab {
  return value !== null && (LEAVE_TABS as string[]).includes(value);
}

/** Confirmation shown after a leave request is accepted by the server. */
export function LeaveNotice({ message }: { message: string }) {
  return (
    <p role="status" className="mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
      {message}
    </p>
  );
}

/** Requests that write attendance and the balance in one step. */
export function LeavePage() {
  const { user } = useAuth();
  const isManager = isManagerOrAbove(user?.role);
  // URL-driven so a tab is shareable and a deep link can open straight to the
  // pending queue; a non-manager asking for the team tab falls back safely.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab: Tab =
    isKnownTab(requested) && (requested !== "team" || isManager) ? requested : "requests";

  function setTab(value: Tab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "requests") next.delete("tab");
        else next.set("tab", value);
        return next;
      },
      { replace: true },
    );
  }

  const tabs: Array<[Tab, string]> = [
    ["requests", "Requests"],
    ["mine", "My leave"],
    ...(isManager ? ([["team", "Team leave"]] as Array<[Tab, string]>) : []),
    ["types", "Leave types"],
    ["balances", "Balances"],
  ];

  return (
    <>
      <PageHeader title="Leave" subtitle="Requests, the balance behind them, and the leave types at play." />
      <div className="mb-5 flex flex-wrap gap-1">
        {tabs.map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={cx(
              "rounded-md px-3 py-1.5 text-sm font-medium transition",
              tab === value
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "requests" ? <LeaveRequestsTab /> : null}
      {tab === "mine" ? <MyLeaveTab /> : null}
      {tab === "team" && isManager ? <TeamLeaveTab /> : null}
      {tab === "types" ? <LeaveTypesTab /> : null}
      {tab === "balances" ? <LeaveBalancesTab /> : null}
    </>
  );
}

function LeaveRequestsTab() {
  const { user } = useAuth();
  const persona = usePersona();
  const isManager = isManagerOrAbove(user?.role);
  // Filing leave is an employee action. The owner keeps full visibility and
  // can intervene on a decision, but must not be offered the employee-only
  // "Request leave" action.
  const canRequestLeave = persona !== "owner";
  const queryClient = useQueryClient();
  // URL-driven so the dashboard can deep-link straight to the pending queue
  // (`/people/leave?status=pending`) and the chosen filter is visible and
  // shareable rather than hidden in component state.
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "";
  const [notice, setNotice] = useState("");

  function setStatus(value: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("status", value);
        else next.delete("status");
        return next;
      },
      { replace: true },
    );
  }

  const requests = useQuery({
    queryKey: ["leave-requests", status],
    queryFn: ({ signal }) => api.leaveRequests({ status: status || undefined, page_size: 100 }, signal),
    placeholderData: (prev) => prev,
  });

  const leaveTypes = useQuery({
    queryKey: ["leave-types", 100],
    queryFn: ({ signal }) => api.leaveTypesPage({ page_size: 100 }, signal),
  });

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    enabled: isManager,
  });

  // The caller's own employee record, so their own pending row is not offered
  // an approve/reject the backend would refuse.
  const me = useQuery({
    queryKey: ["employee-me"],
    queryFn: ({ signal }) => api.employeeMe(signal),
    retry: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["leave-requests"] });
    void queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
  };

  const create = useMutation({
    mutationFn: (body: {
      employee?: string;
      leave_type: string;
      from_date: string;
      to_date: string;
      days?: string;
      reason: string;
    }) =>
      api.createLeaveRequest({
        employee: body.employee ?? "",
        leave_type: body.leave_type,
        from_date: body.from_date,
        to_date: body.to_date,
        days: body.days,
        reason: body.reason,
      }),
    onSuccess: () => {
      invalidate();
      setNotice("Leave request submitted. It now appears in My leave.");
    },
  });

  const decide = useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: "approve" | "reject" | "cancel"; note?: string }) => {
      if (action === "approve") return api.approveLeaveRequest(id, note);
      if (action === "reject") return api.rejectLeaveRequest(id, note);
      return api.cancelLeaveRequest(id);
    },
    onSuccess: invalidate,
  });

  const rows = requests.data?.results ?? [];

  return (
    <>
      {notice ? (
        <LeaveNotice message={notice} />
      ) : null}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1">
          {["", "pending", "approved", "rejected", "cancelled"].map((value) => (
            <button
              key={value || "all"}
              onClick={() => setStatus(value)}
              className={cx(
                "rounded-full px-3 py-1.5 text-xs font-medium capitalize",
                status === value
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
              )}
            >
              {value || "All"}
            </button>
          ))}
        </div>
        <RequestLeaveForm
          isManager={isManager}
          leaveTypes={(leaveTypes.data?.results ?? []).filter((type) => type.is_active !== false)}
          employees={employees.data ?? []}
          pending={create.isPending}
          error={create.error}
          onSubmit={(values) => create.mutateAsync(values)}
          onOpen={() => {
            create.reset();
            setNotice("");
          }}
          canRequest={canRequestLeave}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {requests.isPending ? (
          <TableSkeleton rows={6} />
        ) : requests.isError ? (
          requests.error instanceof ApiError && requests.error.isPlanLimit ? (
            <EmptyState
              icon={<Clock3 size={28} />}
              title="Plan limit reached"
              body={`${requests.error.message} Attendance, leave and payroll come with the Growth plan.`}
            />
          ) : (
            <EmptyState title="Could not load leave requests" body="Please try again." />
          )
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Clock3 size={28} />}
            title={status ? "Nothing in that bucket" : "No leave requests yet"}
            body="Requests appear here and, once approved, write the attendance and the balance in one step."
          />
        ) : (
          <table className="hidden w-full sm:table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="w-12 px-4 py-2.5" />
                <th className="px-4 py-2.5">Employee</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">From</th>
                <th className="px-4 py-2.5">To</th>
                <th className="px-4 py-2.5">Days</th>
                <th className="hidden px-4 py-2.5 lg:table-cell">Requested</th>
                <th className="hidden px-4 py-2.5 xl:table-cell">Approver</th>
                <th className="px-4 py-2.5">Status</th>
                {isManager ? <th className="px-4 py-2.5 text-right" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((request) => (
                <tr key={request.id} className="align-top hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Avatar name={request.employee_name} src={request.employee_photo} size={36} />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/people/employees/${request.employee}`}
                      className="text-sm font-medium text-slate-900 hover:underline"
                    >
                      {request.employee_name}
                    </Link>
                    {request.reason ? (
                      <p className="max-w-xs truncate text-xs text-slate-500">{request.reason}</p>
                    ) : null}
                    {request.status === "pending" && request.approval_available === false ? (
                      <p className="text-xs text-amber-600">
                        No approver: the account has no owner besides the requester
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{request.leave_type_name}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                    {formatDate(request.from_date, user?.account?.timezone)}
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                    {formatDate(request.to_date, user?.account?.timezone)}
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-700">{request.days}</td>
                  <td className="hidden px-4 py-3 text-sm tabular-nums text-slate-500 lg:table-cell">
                    {formatDate(request.created_at, user?.account?.timezone)}
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-slate-600 xl:table-cell">
                    {request.approver_name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={LEAVE_TONE[request.status ?? "pending"]}>{LEAVE_LABEL[request.status ?? "pending"]}</Badge>
                  </td>
                  {isManager ? (
                    <td className="px-4 py-3 text-right">
                      {request.status === "pending" ? (
                        request.can_decide ? (
                          <span className="inline-flex gap-1">
                            <Button
                              variant="ghost"
                              disabled={decide.isPending}
                              onClick={() => decide.mutate({ id: request.id, action: "approve" })}
                              title="Approve"
                              aria-label={`Approve ${request.employee_name}`}
                            >
                              <Check size={15} />
                            </Button>
                            <Button
                              variant="ghost"
                              disabled={decide.isPending}
                              onClick={() => decide.mutate({ id: request.id, action: "reject" })}
                              title="Reject"
                              aria-label={`Reject ${request.employee_name}`}
                            >
                              <X size={15} />
                            </Button>
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">
                            {request.employee === me.data?.id
                              ? "Your request"
                              : request.approver_name
                                ? `With ${request.approver_name}`
                                : "With the owner"}
                          </span>
                        )
                      ) : null}
                    </td>
                  ) : null}
                  {!isManager && request.status === "pending" ? (
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: request.id, action: "cancel" })}
                      >
                        Cancel
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const LEAVE_STATUS_FILTERS = ["", "pending", "approved", "rejected", "cancelled"] as const;

function StatusFilter({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1">
      {LEAVE_STATUS_FILTERS.map((status) => (
        <button
          key={status || "all"}
          onClick={() => onChange(status)}
          className={cx(
            "rounded-full px-3 py-1.5 text-xs font-medium capitalize",
            value === status
              ? "bg-slate-900 text-white"
              : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
          )}
        >
          {status || "All"}
        </button>
      ))}
    </div>
  );
}

/**
 * A manager's own leave: their requests, their balance, their status.
 *
 * Scoped server-side with `scope=mine`, so a manager never sees their reports'
 * rows here even though they may approve them on the Team leave tab. Their own
 * request is routed to their reporting manager by the backend; this screen only
 * files and cancels it.
 */
function MyLeaveTab() {
  const { user } = useAuth();
  const persona = usePersona();
  const timezone = user?.account?.timezone;
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [notice, setNotice] = useState("");
  const currentYear = new Date().getFullYear();

  const me = useQuery({
    queryKey: ["employee-me"],
    queryFn: ({ signal }) => api.employeeMe(signal),
    retry: false,
  });
  const balances = useQuery({
    queryKey: ["my-leave-balances", me.data?.id, currentYear],
    queryFn: ({ signal }) =>
      api.leaveBalances(
        { employee: me.data!.id, year: currentYear, page_size: 100 },
        signal,
      ),
    enabled: Boolean(me.data?.id),
  });
  const requests = useQuery({
    queryKey: ["leave-requests", "mine", status],
    queryFn: ({ signal }) =>
      api.leaveRequests({ scope: "mine", status: status || undefined, page_size: 100 }, signal),
    placeholderData: (prev) => prev,
  });
  const leaveTypes = useQuery({
    queryKey: ["leave-types", 100],
    queryFn: ({ signal }) => api.leaveTypesPage({ page_size: 100 }, signal),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["leave-requests"] });
    void queryClient.invalidateQueries({ queryKey: ["my-leave-balances"] });
  };
  const create = useMutation({
    mutationFn: (body: {
      leave_type: string;
      from_date: string;
      to_date: string;
      days?: string;
      reason: string;
    }) => api.createLeaveRequest({ ...body, employee: "" }),
    onSuccess: () => {
      invalidate();
      setNotice("Leave request submitted. It now appears below.");
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelLeaveRequest(id),
    onSuccess: invalidate,
  });

  const rows = requests.data?.results ?? [];
  const balanceRows = balances.data?.results ?? [];
  const balanceByType = new Map(
    balanceRows.map((balance) => [balance.leave_type, balance]),
  );
  const quotaCodes = ["CL", "SL", "PL"] as const;
  const quotaTypes = (leaveTypes.data?.results ?? []).filter((type) =>
    (quotaCodes as readonly string[]).includes(type.code),
  );
  const hasAnyBalance = balanceRows.length > 0;
  const missingQuota = quotaTypes.some(
    (type) => !balanceByType.has(type.id),
  );
  const noEmployee = me.isError;
  const missingApprover = rows.some((row) => row.status === "pending" && row.approval_available === false);

  return (
    <>
      {notice ? (
        <LeaveNotice message={notice} />
      ) : null}
      {noEmployee ? (
        <p role="alert" className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          No employee record is linked to your login, so you cannot file or be routed leave here. Ask an
          administrator to link your account to an employee record.
        </p>
      ) : null}
      {missingApprover ? (
        <p role="alert" className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          You have a pending request that nobody can decide: this account has no owner or manager
          besides you. It stays unassigned until an owner is available.
        </p>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <StatusFilter value={status} onChange={setStatus} />
        {!noEmployee ? (
          <RequestLeaveForm
            isManager={false}
            leaveTypes={(leaveTypes.data?.results ?? []).filter((type) => type.is_active !== false)}
            employees={[]}
            pending={create.isPending}
            error={create.error}
            onSubmit={(values) => create.mutateAsync(values)}
            onOpen={() => {
              create.reset();
              setNotice("");
            }}
            canRequest={persona !== "owner" && balances.isSuccess && hasAnyBalance}
          />
        ) : null}
      </div>

      <Card className="mb-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-900">My balance</h2>
        {balances.isPending ? (
          <Skeleton className="h-14 w-full" />
        ) : balances.isError ? (
          <p role="alert" className="text-sm text-rose-600">
            {leaveErrorMessage(balances.error)}
          </p>
        ) : !hasAnyBalance ? (
          <p role="status" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            Your leave balance has not been configured yet. Please contact your Owner or HR administrator.
          </p>
        ) : (
          <>
            <ul className="grid gap-2 sm:grid-cols-3">
              {quotaTypes.map((type) => {
                const balance = balanceByType.get(type.id);
                return (
                  <li
                    key={type.id}
                    className="rounded-md border border-slate-200 px-3 py-2"
                  >
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {type.code} · {type.name}
                    </p>
                    {balance ? (
                      <>
                        <p className="text-lg font-semibold tabular-nums text-slate-900">
                          {`${formatDays(balance.available_days)} left`}
                        </p>
                        <p className="text-xs tabular-nums text-slate-500">
                          {`${formatDays(balance.total_days)} allocated · ${formatDays(
                            balance.utilized_days,
                          )} used · ${formatDays(balance.pending_days)} pending`}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">Not configured</p>
                    )}
                  </li>
                );
              })}
            </ul>
            {missingQuota ? (
              <p className="mt-3 text-xs text-slate-500">
                Some leave quotas are not configured for {currentYear}. Ask your Owner or HR
                administrator to initialise them before requesting those leave types.
              </p>
            ) : null}
          </>
        )}
      </Card>

      <LeaveRequestTable
        rows={rows}
        loading={requests.isPending}
        error={requests.error}
        timezone={timezone}
        emptyTitle={status ? "Nothing in that bucket" : "You have not filed leave yet"}
        emptyBody="File a request and it is routed to your reporting manager -- or to the owner when you have none -- for a decision."
        renderActions={(request) =>
          request.status === "pending" ? (
            <Button variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate(request.id)}>
              Cancel
            </Button>
          ) : null
        }
      />
    </>
  );
}

/**
 * Team leave: the manager's direct reports, decided in place.
 *
 * `scope=team` returns only people whose Reports To is the caller, and the API
 * refuses an approval from anyone else -- the frontend is a convenience, the
 * queryset is the control.
 */
function TeamLeaveTab() {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStatus = searchParams.get("team_status");
  const status =
    rawStatus === null ? "pending" : rawStatus === "all" ? "" : rawStatus;
  const [notice, setNotice] = useState("");

  function setStatus(value: string) {
    setNotice("");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("team_status", value || "all");
        return next;
      },
      { replace: true },
    );
  }

  const requests = useQuery({
    queryKey: ["leave-requests", "team", status],
    queryFn: ({ signal }) =>
      api.leaveRequests({ scope: "team", status: status || undefined, page_size: 100 }, signal),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["leave-requests"] });
    void queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
    void queryClient.invalidateQueries({ queryKey: ["my-leave-balances"] });
  };
  const decide = useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: "approve" | "reject"; note?: string }) =>
      action === "approve" ? api.approveLeaveRequest(id, note) : api.rejectLeaveRequest(id, note),
    onSuccess: (_request, variables) => {
      invalidate();
      setNotice(
        variables.action === "approve"
          ? "Leave request approved."
          : "Leave request rejected.",
      );
    },
  });

  const rows = requests.data?.results ?? [];

  return (
    <>
      {notice ? <LeaveNotice message={notice} /> : null}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <StatusFilter value={status} onChange={setStatus} />
        {requests.isSuccess ? (
          <span className="text-xs text-slate-500">
            {rows.length} request{rows.length === 1 ? "" : "s"} from your team
          </span>
        ) : null}
      </div>

      {decide.error ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {leaveErrorMessage(decide.error)}
        </p>
      ) : null}

      <LeaveRequestTable
        rows={rows}
        loading={requests.isPending}
        error={requests.error}
        timezone={timezone}
        emptyTitle={status ? "Nothing in that bucket" : "No team requests yet"}
        emptyBody="Requests from the people who report to you land here for a decision."
        emptyAction={
          status ? (
            <Button onClick={() => setStatus("")}>View all requests</Button>
          ) : undefined
        }
        renderActions={(request) =>
          request.status === "pending" ? (
            request.can_decide ? (
              <DecisionControl
                request={request}
                pending={decide.isPending}
                onDecide={(action, note) => decide.mutate({ id: request.id, action, note })}
              />
            ) : (
              <span className="text-xs text-slate-400">
                {request.approver_name ? `With ${request.approver_name}` : "With the owner"}
              </span>
            )
          ) : (
            <span className="text-xs text-slate-400">{request.decided_by_name || ""}</span>
          )
        }
      />
    </>
  );
}

function DecisionControl({
  request,
  pending,
  onDecide,
}: {
  request: LeaveRequest;
  pending: boolean;
  onDecide: (action: "approve" | "reject", note: string) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");

  if (rejecting) {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onDecide("reject", note.trim());
          setRejecting(false);
          setNote("");
        }}
        className="flex items-center justify-end gap-1"
      >
        <input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Reason (optional)"
          aria-label={`Reason for rejecting ${request.employee_name}`}
          className="w-36 rounded-md border-0 px-2 py-1 text-xs ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
        <Button type="submit" variant="ghost" disabled={pending} title="Confirm rejection" aria-label="Confirm rejection">
          <Check size={15} />
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setRejecting(false);
            setNote("");
          }}
          title="Cancel"
          aria-label="Cancel rejection"
        >
          <X size={15} />
        </Button>
      </form>
    );
  }

  return (
    <span className="inline-flex gap-1">
      <Button
        variant="ghost"
        disabled={pending}
        onClick={() => onDecide("approve", "")}
        title="Approve"
        aria-label={`Approve ${request.employee_name}`}
      >
        <Check size={15} />
      </Button>
      <Button
        variant="ghost"
        disabled={pending}
        onClick={() => setRejecting(true)}
        title="Reject"
        aria-label={`Reject ${request.employee_name}`}
      >
        <X size={15} />
      </Button>
    </span>
  );
}

function LeaveRequestTable({
  rows,
  loading,
  error,
  timezone,
  emptyTitle,
  emptyBody,
  emptyAction,
  renderActions,
}: {
  rows: LeaveRequest[];
  loading: boolean;
  error: unknown;
  timezone?: string | null;
  emptyTitle: string;
  emptyBody: string;
  emptyAction?: React.ReactNode;
  renderActions: (request: LeaveRequest) => React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
      {loading ? (
        <TableSkeleton rows={6} />
      ) : error ? (
        error instanceof ApiError && error.isPlanLimit ? (
          <EmptyState
            icon={<Clock3 size={28} />}
            title="Plan limit reached"
            body={`${error.message} Attendance, leave and payroll come with the Growth plan.`}
          />
        ) : (
          <EmptyState
            title="Could not load leave requests"
            body={error instanceof ApiError ? error.message : "Please try again."}
          />
        )
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Clock3 size={28} />}
          title={emptyTitle}
          body={emptyBody}
          action={emptyAction}
        />
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="w-12 px-4 py-2.5" />
              <th className="px-4 py-2.5">Employee</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">From</th>
              <th className="hidden px-4 py-2.5 sm:table-cell">To</th>
              <th className="px-4 py-2.5">Days</th>
              <th className="hidden px-4 py-2.5 lg:table-cell">Requested</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((request) => (
              <tr key={request.id} className="align-top hover:bg-slate-50">
                <td className="px-4 py-3">
                  <Avatar name={request.employee_name} src={request.employee_photo} size={36} />
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/people/employees/${request.employee}`}
                    className="text-sm font-medium text-slate-900 hover:underline"
                  >
                    {request.employee_name}
                  </Link>
                  {request.reason ? (
                    <p className="max-w-xs truncate text-xs text-slate-500" title={request.reason}>
                      {request.reason}
                    </p>
                  ) : null}
                  {request.status === "pending" && request.approval_available === false ? (
                    <p className="text-xs text-amber-600">No approver: no owner besides the requester</p>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{request.leave_type_name}</td>
                <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                  {formatDate(request.from_date, timezone ?? undefined)}
                </td>
                <td className="hidden px-4 py-3 text-sm tabular-nums text-slate-600 sm:table-cell">
                  {formatDate(request.to_date, timezone ?? undefined)}
                </td>
                <td className="px-4 py-3 text-sm tabular-nums text-slate-700">{request.days}</td>
                <td className="hidden px-4 py-3 text-sm tabular-nums text-slate-500 lg:table-cell">
                  {formatDate(request.created_at, timezone ?? undefined)}
                </td>
                <td className="px-4 py-3">
                  <Badge tone={LEAVE_TONE[request.status ?? "pending"]}>{LEAVE_LABEL[request.status ?? "pending"]}</Badge>
                </td>
                <td className="px-4 py-3 text-right">{renderActions(request)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function RequestLeaveForm({
  isManager,
  leaveTypes,
  employees,
  pending,
  error,
  onSubmit,
  onOpen,
  canRequest = true,
  initialOpen = false,
}: {
  isManager: boolean;
  leaveTypes: LeaveType[];
  employees: Array<{ id: string; display_name: string }>;
  pending: boolean;
  error: unknown;
  onSubmit: (values: {
    employee?: string;
    leave_type: string;
    from_date: string;
    to_date: string;
    days?: string;
    reason: string;
  }) => Promise<unknown>;
  onOpen?: () => void;
  canRequest?: boolean;
  initialOpen?: boolean;
}) {
  const [open, setOpen] = useState(initialOpen);
  const [employee, setEmployee] = useState("");
  const [leaveType, setLeaveType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [days, setDays] = useState("");
  const [daysTouched, setDaysTouched] = useState(false);
  const [reason, setReason] = useState("");
  const [daysError, setDaysError] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const busy = pending || submitting;

  // The days asked for default to the whole range; a half day is asked for
  // explicitly, because "one day" and "half a day" spend different balances
  // and write different attendance rows.
  const spanDays = from && to ? calendarDays(from, to) : 0;
  const daysValue = daysTouched ? Number(days) : spanDays;

  // The owner sees the register and can intervene, but does not file leave.
  if (!canRequest) return null;

  if (!open) {
    return (
      <Button
        variant="primary"
        onClick={() => {
          onOpen?.();
          setOpen(true);
        }}
      >
        <Plus size={15} /> Request leave
      </Button>
    );
  }

  return (
    <Card padded={false} className="w-full p-4">
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (busy) return;
          if (!leaveType || !from || !to) return;
          if (daysValue <= 0 || daysValue > spanDays) {
            setDaysError(
              `Days must be more than zero and no more than the ${spanDays} day(s) in the range.`,
            );
            return;
          }
          if (!reason.trim()) {
            setReasonError("Enter a reason for the leave request.");
            return;
          }
          setDaysError("");
          setReasonError("");
          setSubmitting(true);
          try {
            await onSubmit({
              employee: employee || undefined,
              leave_type: leaveType,
              from_date: from,
              to_date: to,
              days: String(daysValue),
              reason,
            });
            setOpen(false);
            setLeaveType("");
            setFrom("");
            setTo("");
            setDays("");
            setDaysTouched(false);
            setDaysError("");
            setReasonError("");
            setReason("");
          } catch {
            // The mutation error is rendered from the `error` prop. Keep the
            // form open with the employee's input so they can correct it.
          } finally {
            setSubmitting(false);
          }
        }}
        className="grid gap-3 sm:grid-cols-2"
      >
        {isManager ? (
          <Field label="Employee">
            <select
              value={employee}
              onChange={(event) => setEmployee(event.target.value)}
              className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300"
            >
              <option value="">Choose…</option>
              {employees.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.display_name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label="Leave type">
          <select
            value={leaveType}
            onChange={(event) => setLeaveType(event.target.value)}
            required
            className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300"
          >
            <option value="">Choose…</option>
            {leaveTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="From">
          <input
            type="date"
            value={from}
            required
            onChange={(event) => setFrom(event.target.value)}
            className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={to}
            required
            onChange={(event) => setTo(event.target.value)}
            className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300"
          />
        </Field>
        <Field label="Days" hint={`${spanDays} day(s) in the range`}>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={daysTouched ? days : spanDays ? String(spanDays) : ""}
              onChange={(event) => {
                setDaysTouched(true);
                setDays(event.target.value);
              }}
              aria-label="Days"
              className="w-24 rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300"
            />
            <button
              type="button"
              onClick={() => {
                setDaysTouched(true);
                setDays("0.5");
              }}
              disabled={spanDays !== 1}
              title={spanDays === 1 ? "Request half a day" : "A half day needs a single-day range"}
              className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Half day
            </button>
          </div>
        </Field>
        <Field label="Reason">
          <input
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError("");
            }}
            required
            aria-label="Reason"
            aria-invalid={reasonError ? true : undefined}
            className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300"
          />
          {reasonError ? (
            <span role="alert" className="mt-1 block text-xs text-rose-600">
              {reasonError}
            </span>
          ) : null}
        </Field>
        {error ? (
          <p role="alert" className="text-sm text-rose-600 sm:col-span-2">
            {leaveErrorMessage(error)}
          </p>
        ) : null}
        {daysError ? (
          <p role="alert" className="text-sm text-rose-600 sm:col-span-2">
            {daysError}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 sm:col-span-2">
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

/** Inclusive calendar days between two ISO dates. */
function calendarDays(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.round((end - start) / 86_400_000) + 1;
}

function LeaveTypesTab() {
  const { user } = useAuth();
  const isManager = isManagerOrAbove(user?.role);
  const queryClient = useQueryClient();

  const types = useQuery({
    queryKey: ["leave-types", 100],
    queryFn: ({ signal }) => api.leaveTypesPage({ page_size: 100 }, signal),
  });

  const create = useMutation({
    mutationFn: (body: {
      name: string;
      code: string;
      is_paid?: boolean;
      annual_days?: string;
      accrues_monthly?: boolean;
      carry_forward_days?: string;
      is_active?: boolean;
    }) => api.createLeaveType(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["leave-types"] }),
  });

  const rows = types.data?.results ?? [];

  return (
    <>
      {isManager ? (
        <AddLeaveType pending={create.isPending} onSubmit={(values) => create.mutate(values)} />
      ) : null}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {types.isPending ? (
          <TableSkeleton rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState title="No leave types yet" body="Types like 'Casual' and 'Sick' set the annual allowance and carry-forward." />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Annual</th>
                <th className="px-4 py-2.5">Carry over</th>
                <th className="px-4 py-2.5">Paid</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((type) => (
                <tr key={type.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-900">{type.name}</p>
                    <p className="text-xs text-slate-500">{type.code}</p>
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-700">{type.annual_days ?? 0}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">{type.carry_forward_days ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{type.is_paid ? "Paid" : "Unpaid"}</td>
                  <td className="px-4 py-3">
                    <Badge tone={type.is_active ? "emerald" : "slate"}>
                      {type.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-500">Balances accrue against the year in {new Date().getFullYear()}.</p>
    </>
  );
}

function AddLeaveType({
  pending,
  onSubmit,
}: {
  pending: boolean;
  onSubmit: (values: { name: string; code: string; annual_days?: string; carry_forward_days?: string; is_paid?: boolean }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [annualDays, setAnnualDays] = useState("10");
  const [carryForward, setCarryForward] = useState("0");
  const [isPaid, setIsPaid] = useState(true);

  if (!open) {
    return (
      <div className="mb-4">
        <Button variant="primary" onClick={() => setOpen(true)}>
          <Plus size={15} /> Add leave type
        </Button>
      </div>
    );
  }

  return (
    <Card padded={false} className="mb-4 p-4">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim() || !code.trim()) return;
          onSubmit({
            name: name.trim(),
            code: code.trim(),
            annual_days: annualDays,
            carry_forward_days: carryForward,
            is_paid: isPaid,
          });
          setName("");
          setCode("");
          setOpen(false);
        }}
        className="grid gap-3 sm:grid-cols-3"
      >
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900" />
        </Field>
        <Field label="Code">
          <input value={code} onChange={(e) => setCode(e.target.value)} required className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900" />
        </Field>
        <Field label="Annual days">
          <input type="number" min="0" value={annualDays} onChange={(e) => setAnnualDays(e.target.value)} className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900" />
        </Field>
        <Field label="Carry forward days">
          <input type="number" min="0" value={carryForward} onChange={(e) => setCarryForward(e.target.value)} className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900" />
        </Field>
        <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
          <input type="checkbox" checked={isPaid} onChange={() => setIsPaid(!isPaid)} className="size-4 accent-slate-900" />
          Paid leave
        </label>
        <div className="flex justify-end gap-2">
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

type QuotaCode = "CL" | "SL" | "PL";

const QUOTA_CODES: QuotaCode[] = ["CL", "SL", "PL"];

const QUOTA_LABELS: Record<QuotaCode, string> = {
  CL: "Casual Leave (CL)",
  SL: "Sick Leave (SL)",
  PL: "Privilege Leave (PL)",
};

/** Suggested first-year allocation; authorised users can change every value. */
const QUOTA_DEFAULTS: Record<QuotaCode, string> = {
  CL: "12",
  SL: "10",
  PL: "15",
};

type QuotaValues = Record<QuotaCode, string>;

function leaveErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fieldMessages = Object.values(error.fields).flat().filter(Boolean);
    if (fieldMessages.length > 0) return fieldMessages.join(" ");
    return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "The leave request could not be submitted. Please try again.";
}

function formatDays(value: number | string | null | undefined): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  return Number.isInteger(numeric)
    ? String(numeric)
    : numeric.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function cursorFromUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url, "http://localhost").searchParams.get("cursor") ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * The owner/manager workspace for an employee's annual leave bank.
 *
 * The table is employee-centric: one row per person, with the CL/SL/PL
 * balance the ledger already computes. Assignment is creation-only; an
 * existing row is edited in place so used and pending days are never reset.
 */
function LeaveBalancesTab() {
  const { user } = useAuth();
  const canManage = isManagerOrAbove(user?.role);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<LeaveBalanceAuditRow | null>(null);
  const [formYear, setFormYear] = useState(year);
  const [formValues, setFormValues] = useState<QuotaValues>({ ...QUOTA_DEFAULTS });
  const [formError, setFormError] = useState<string | null>(null);

  const types = useQuery({
    queryKey: ["leave-types", 100],
    queryFn: ({ signal }) => api.leaveTypesPage({ page_size: 100 }, signal),
  });
  const audit = useQuery({
    queryKey: ["leave-balance-audit", year, search, cursor ?? null],
    queryFn: ({ signal }) =>
      api.leaveBalanceAudit(
        {
          year,
          search: search || undefined,
          cursor,
          page_size: 25,
        },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const rows = audit.data?.results ?? [];
  const nextCursor = cursorFromUrl(audit.data?.next);
  const previousCursor = cursorFromUrl(audit.data?.previous);

  function changeSearch(value: string) {
    setSearch(value);
    setCursor(undefined);
  }

  function changeYear(value: number) {
    setYear(value);
    setCursor(undefined);
  }

  function typeFor(code: QuotaCode): LeaveType | undefined {
    return (types.data?.results ?? []).find((type) => type.code === code);
  }

  function balancesFor(employee: LeaveBalanceAuditRow): LeaveBalance[] {
    return employee.balances ?? [];
  }

  function openManage(employee: LeaveBalanceAuditRow) {
    const existing = balancesFor(employee);
    const next = { ...QUOTA_DEFAULTS };
    for (const code of QUOTA_CODES) {
      const type = typeFor(code);
      const balance = type
        ? existing.find((row) => row.leave_type === type.id)
        : undefined;
      if (balance) next[code] = formatDays(balance.credited_days);
    }
    setSelected(employee);
    setFormYear(year);
    setFormValues(next);
    setFormError(null);
  }

  const save = useMutation({
    mutationFn: async ({
      employee,
      year: effectiveYear,
      values,
    }: {
      employee: LeaveBalanceAuditRow;
      year: number;
      values: QuotaValues;
    }) => {
      const page = await api.leaveBalances({
        employee: employee.id,
        year: effectiveYear,
        page_size: 200,
      });
      const existing = new Map(
        page.results.map((balance) => [balance.leave_type, balance]),
      );
      const items: LeaveQuotaItem[] = [];
      const updates: Array<{ id: string; days: string }> = [];

      for (const code of QUOTA_CODES) {
        const type = typeFor(code);
        if (!type) {
          throw new Error(
            `${QUOTA_LABELS[code]} is not configured. Add the leave type first.`,
          );
        }
        const raw = values[code].trim();
        const days = Number(raw);
        if (raw === "" || !Number.isFinite(days) || days < 0) {
          throw new Error(`${QUOTA_LABELS[code]} must be zero or more days.`);
        }
        const balance = existing.get(type.id);
        if (balance) updates.push({ id: balance.id, days: String(days) });
        else items.push({ leave_type: type.id, days: String(days) });
      }

      if (items.length) {
        await api.assignLeaveQuota({
          employee: employee.id,
          year: effectiveYear,
          items,
        });
      }
      for (const update of updates) {
        await api.updateLeaveBalance(update.id, {
          credited_days: update.days,
        });
      }
    },
    onSuccess: (_data, variables) => {
      setSelected(null);
      setYear(variables.year);
      setCursor(undefined);
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ["leave-balance-audit"] });
      void queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
    },
    onError: (error) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Could not save the leave quota.",
      );
    },
  });

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            Effective year
          </span>
          <input
            type="number"
            value={year}
            onChange={(event) => changeYear(Number(event.target.value))}
            className="w-28 rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </label>
        <label className="min-w-64 flex-1 text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            Search employees
          </span>
          <span className="relative block">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(event) => changeSearch(event.target.value)}
              placeholder="Search by name or employee ID"
              aria-label="Search employees by name or employee ID"
              className="w-full rounded-md border-0 py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            />
          </span>
        </label>
      </div>

      {selected ? (
        <Card className="mb-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setFormError(null);
              save.mutate({ employee: selected, year: formYear, values: formValues });
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">
                Assign Leave Quota
              </h2>
              <Button
                onClick={() => setSelected(null)}
                aria-label="Close assign leave quota"
              >
                <X size={15} />
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Employee">
                <input
                  readOnly
                  value={`${selected.display_name} (${selected.employee_code})`}
                  className="w-full rounded-md border-0 bg-slate-50 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300"
                />
              </Field>
              <Field label="Effective year">
                <input
                  type="number"
                  min="2000"
                  max="2100"
                  value={formYear}
                  onChange={(event) => setFormYear(Number(event.target.value))}
                  className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
                />
              </Field>
              {QUOTA_CODES.map((code) => (
                <Field key={code} label={QUOTA_LABELS[code]}>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={formValues[code]}
                    onChange={(event) =>
                      setFormValues((current) => ({
                        ...current,
                        [code]: event.target.value,
                      }))
                    }
                    className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
                  />
                </Field>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Only approved leave reduces the available balance; pending requests
              are held separately. An existing allocation is adjusted without
              touching used or pending days.
            </p>
            {formError ? (
              <p role="alert" className="mt-3 text-sm text-rose-600">
                {formError}
              </p>
            ) : null}
            <div className="mt-3 flex justify-end gap-2">
              <Button onClick={() => setSelected(null)}>Cancel</Button>
              <Button type="submit" variant="primary" disabled={save.isPending}>
                Save
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {audit.isPending ? (
          <TableSkeleton rows={5} />
        ) : audit.isError ? (
          <EmptyState
            title="Could not load leave balances"
            body="Please try again."
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={search ? "No employees match that search" : "No employees yet"}
            body={
              search
                ? "Try a different name or employee ID."
                : "Add an employee before assigning leave quotas."
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Employee</th>
                  <th className="px-4 py-2.5">Department</th>
                  {QUOTA_CODES.map((code) => (
                    <th key={code} className="px-4 py-2.5">
                      {code}
                    </th>
                  ))}
                  <th className="px-4 py-2.5 text-right">Total remaining</th>
                  <th className="px-4 py-2.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((employee) => {
                  const employeeBalances = balancesFor(employee);
                  const hasAny = employeeBalances.length > 0;
                  const totalRemaining = QUOTA_CODES.reduce((total, code) => {
                    const type = typeFor(code);
                    const balance = type
                      ? employeeBalances.find(
                          (row) => row.leave_type === type.id,
                        )
                      : undefined;
                    return total + (balance ? Number(balance.available_days) : 0);
                  }, 0);

                  return (
                    <tr key={employee.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">
                          {employee.display_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {employee.employee_code}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {employee.department || "—"}
                      </td>
                      {QUOTA_CODES.map((code) => {
                        const type = typeFor(code);
                        const balance = type
                          ? employeeBalances.find(
                              (row) => row.leave_type === type.id,
                            )
                          : undefined;
                        if (!balance) {
                          return (
                            <td
                              key={code}
                              className="px-4 py-3 text-sm text-slate-400"
                            >
                              Not assigned
                            </td>
                          );
                        }
                        return (
                          <td key={code} className="px-4 py-3">
                            <p className="text-sm font-medium tabular-nums text-slate-900">
                              {`${formatDays(balance.available_days)} left`}
                            </p>
                            <p className="text-[11px] tabular-nums text-slate-500">
                              {`${formatDays(balance.total_days)} allocated · ${formatDays(
                                balance.utilized_days,
                              )} used${
                                Number(balance.pending_days) > 0
                                  ? ` · ${formatDays(balance.pending_days)} pending`
                                  : ""
                              }`}
                            </p>
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-right text-sm font-medium tabular-nums text-slate-900">
                        {hasAny ? `${formatDays(totalRemaining)} days` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canManage ? (
                          <Button onClick={() => openManage(employee)}>
                            {hasAny ? "Manage balances" : "Assign Leave Quota"}
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">
                            View only
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!audit.isPending && !audit.isError && rows.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
            <span className="text-xs text-slate-500">
              {`${rows.length} employee${rows.length === 1 ? "" : "s"} on this page`}
            </span>
            <div className="flex gap-2">
              <Button
                disabled={!previousCursor}
                onClick={() => setCursor(previousCursor)}
              >
                Previous
              </Button>
              <Button disabled={!nextCursor} onClick={() => setCursor(nextCursor)}>
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
